import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { PageHeader } from '../../../components/ui/PageHeader'
import { pb } from '../../../pocketbase'
import { COL_CONSUMABLE_CATEGORIES, COL_CONSUMABLES } from '../../../config/collections'
import { PERMISSION_KEYS } from '../../../config/permissions'
import { useAuth } from '../../../context/AuthContext'
import { matchesListSearch } from '../../../lib/listSearch'
import { unitLabel } from '../../../lib/units'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import {
  alert,
  btn,
  cn,
  productionMainClass,
  productionWithCategoriesClass,
  searchInputClass,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../../lib/ui'
import type { ConsumableRecord } from '../../../types/records'
import { CategoryTreePanel, type ProductionCategoryFilter } from './CategoryTreePanel'

export function ConsumablesListPage() {
  const { can } = useAuth()
  const [items, setItems] = useState<ConsumableRecord[]>([])
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)
  const [catFilter, setCatFilter] = useState<ProductionCategoryFilter>({ kind: 'all' })
  const [searchQuery, setSearchQuery] = useState('')

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const list = await pb.collection(COL_CONSUMABLES).getFullList<ConsumableRecord>({
        sort: 'name',
        expand: 'consumable_category',
      })
      setItems(list)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const byCategory = useMemo(() => {
    if (catFilter.kind === 'all') return items
    if (catFilter.kind === 'uncategorized') return items.filter((i) => !i.consumable_category)
    const set = new Set(catFilter.ids)
    return items.filter((i) => i.consumable_category && set.has(i.consumable_category))
  }, [items, catFilter])

  const displayedItems = useMemo(() => {
    if (!searchQuery.trim()) return byCategory
    return byCategory.filter((row) =>
      matchesListSearch(
        searchQuery,
        row.name,
        row.code,
        row.expand?.consumable_category?.name,
        row.notes,
        row.unit,
      ),
    )
  }, [byCategory, searchQuery])

  const newConsumableTo = useMemo(() => {
    if (catFilter.kind === 'subtree') {
      return `/production/consumables/new?category=${encodeURIComponent(catFilter.rootId)}`
    }
    return '/production/consumables/new'
  }, [catFilter])

  async function removeRow(id: string) {
    if (!window.confirm('Удалить расходник?')) return
    try {
      await pb.collection(COL_CONSUMABLES).delete(id)
      await load()
    } catch (e: unknown) {
      window.alert(pocketbaseErrorMessage(e, 'Ошибка'))
    }
  }

  const canManageCategories =
    can(PERMISSION_KEYS.CONSUMABLES_WRITE) || can(PERMISSION_KEYS.PRODUCTS_WRITE)

  return (
    <div className={productionWithCategoriesClass}>
      <div className={productionMainClass}>
        <PageHeader
          asSection
          title="Расходники"
          lead="Справочник материалов: цена за 1 единицу указанной меры (м, кг, шт, л и т.д.)."
        />
        {err && <div className={alert('err')}>{err}</div>}
        <div className={toolbarClass}>
          {can(PERMISSION_KEYS.CONSUMABLES_WRITE) && (
            <Link to={newConsumableTo} className={btn('primary')}>
              Добавить расходник
            </Link>
          )}
          <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
            Обновить
          </button>
          <div className="ml-auto min-w-0 w-full max-w-sm sm:w-auto">
            <input
              type="search"
              className={searchInputClass}
              placeholder="Поиск: название, код, категория…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Поиск по расходникам"
            />
          </div>
        </div>
        <div className={tableWrapClass}>
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={tableHeadClass}>Наименование</th>
                <th className={tableHeadClass}>Категория</th>
                <th className={tableHeadClass}>Код</th>
                <th className={tableHeadClass}>Ед. изм.</th>
                <th className={tableHeadClass}>Цена за ед.</th>
                <th className={tableHeadClass} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td className={tableCellClass} colSpan={6}>
                    <em>Загрузка…</em>
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td className={tableCellClass} colSpan={6}>
                    <em>Нет записей — добавьте скотч, бумагу и т.п.</em>
                  </td>
                </tr>
              )}
              {!loading &&
                items.length > 0 &&
                byCategory.length === 0 && (
                <tr>
                  <td className={tableCellClass} colSpan={6}>
                    <em>В выбранной категории ничего нет.</em>
                  </td>
                </tr>
              )}
              {!loading &&
                byCategory.length > 0 &&
                displayedItems.length === 0 && (
                <tr>
                  <td className={tableCellClass} colSpan={6}>
                    <em>По запросу ничего не найдено — измените текст поиска.</em>
                  </td>
                </tr>
              )}
              {!loading &&
                displayedItems.map((row) => (
                  <tr key={row.id} className={tableRowHoverClass}>
                    <td className={tableCellClass}>{row.name}</td>
                    <td className={tableCellClass}>{row.expand?.consumable_category?.name ?? '—'}</td>
                    <td className={tableCellClass}>{row.code || '—'}</td>
                    <td className={tableCellClass}>{unitLabel(row.unit)}</td>
                    <td className={tableCellClass}>
                      {Number(row.price).toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
                    </td>
                    <td className={cn(tableCellClass, 'whitespace-nowrap')}>
                      {can(PERMISSION_KEYS.CONSUMABLES_WRITE) && (
                        <Link to={`/production/consumables/${row.id}`} className={btn('ghost', 'sm')}>
                          Изменить
                        </Link>
                      )}
                      {can(PERMISSION_KEYS.CONSUMABLES_DELETE) && (
                        <button
                          type="button"
                          className={cn(btn('danger', 'sm'), 'ml-1')}
                          onClick={() => void removeRow(row.id)}
                        >
                          Удалить
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <CategoryTreePanel
        categoryCollection={COL_CONSUMABLE_CATEGORIES}
        title="Категории комплектации"
        value={catFilter}
        onChange={setCatFilter}
        canManage={canManageCategories}
      />
    </div>
  )
}
